import { CSSProperties, useEffect, useState } from 'react';
import { SAvatar } from '@setvi/shared/components';
import type { SAvatarProps } from '@setvi/shared/components/savatar';
import { getRevopsProfileImage } from '@setvi/shared/utils';
import { useAppContext } from 'Providers/AppProvider/AppContext';

interface RfqUserAvatarProps {
  /** User id used to build the RevOps profile image URL. */
  userId?: string | number | null;
  /** Initials — used for the colored fallback and as the displayed text. */
  value: string;
  /** Defaults to the current user's company (RFQ collaborators are same-company). */
  companyId?: string | number | null;
  size?: SAvatarProps['size'];
  className?: string;
  style?: CSSProperties;
}

/**
 * Avatar for RFQ users: shows the RevOps profile photo when it exists, and
 * falls back to the string-to-color initials avatar when the image is missing
 * or fails to load.
 */
export const RfqUserAvatar = ({
  userId,
  value,
  companyId,
  size = 'sm',
  className,
  style
}: RfqUserAvatarProps) => {
  const { user } = useAppContext();
  const [errored, setErrored] = useState(false);

  const resolvedCompanyId = companyId ?? user?.CompanyID;
  const src = getRevopsProfileImage(resolvedCompanyId, userId);
  const showImage = !!src && !errored;

  // Reset the error flag when the target image changes.
  useEffect(() => setErrored(false), [src]);

  return (
    <SAvatar
      size={size}
      className={className}
      value={value}
      src={showImage ? src : undefined}
      imgProps={{ onError: () => setErrored(true) }}
      // White backdrop behind (possibly transparent) photos; the colored
      // initials background is handled by SAvatar when there's no image.
      style={{
        ...(showImage ? { backgroundColor: '#FFFFFF' } : {}),
        ...style
      }}
    />
  );
};
